import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'branding', label: 'Brend', icon: '🏷️' },
  { id: 'navbar', label: 'Navbar', icon: '🧭' },
  { id: 'hero', label: 'Hero', icon: '🎯' },
  { id: 'mockup', label: 'Mockup', icon: '📱' },
  { id: 'features', label: 'Xüsusiyyətlər', icon: '✨' },
  { id: 'how', label: 'Necə işləyir', icon: '🔄' },
  { id: 'contact', label: 'Əlaqə', icon: '📞' },
  { id: 'footer', label: 'Footer', icon: '🦶' },
  { id: 'scripts', label: 'Trekkerlər', icon: '📊' },
  { id: 'socials', label: 'Sosial', icon: '💬' },
];

const SOCIAL_PLATFORMS = [
  { key: 'twitter', name: 'X (Twitter)', example: 'https://x.com/smartmenyu' },
  { key: 'instagram', name: 'Instagram', example: 'https://instagram.com/smartmenyu' },
  { key: 'facebook', name: 'Facebook', example: 'https://facebook.com/smartmenyu' },
  { key: 'whatsapp', name: 'WhatsApp', example: 'https://wa.me/994551234567' },
  { key: 'linkedin', name: 'LinkedIn', example: 'https://linkedin.com/company/smartmenyu' },
  { key: 'youtube', name: 'YouTube', example: 'https://youtube.com/@smartmenyu' },
  { key: 'spotify', name: 'Spotify', example: 'https://open.spotify.com/user/smartmenyu' },
  { key: 'tiktok', name: 'TikTok', example: 'https://tiktok.com/@smartmenyu' },
  { key: 'telegram', name: 'Telegram', example: 'https://t.me/smartmenyu' },
];

const COLORS = [
  'from-emerald-500 to-emerald-700',
  'from-blue-500 to-blue-700',
  'from-amber-500 to-amber-700',
  'from-purple-500 to-purple-700',
  'from-red-500 to-red-700',
  'from-pink-500 to-pink-700',
  'from-indigo-500 to-indigo-700',
  'from-teal-500 to-teal-700',
  'from-orange-500 to-orange-700',
  'from-cyan-500 to-cyan-700',
];

export const LandingAdmin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [activeSection, setActiveSection] = useState('branding');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/site/content`);
      setContent(data);
    } catch (err) {
      setStatus({ type: 'error', text: 'Məzmun yüklənə bilmədi' });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await axios.put(`${BACKEND_URL}/api/site/content`, content, { withCredentials: true });
      setStatus({ type: 'success', text: '✅ Uğurla yadda saxlanıldı!' });
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setStatus({ type: 'error', text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Bütün dəyişikliklər silinəcək və default məzmuna qaytarılacaq. Əminsiniz?')) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/site/content/reset`, {}, { withCredentials: true });
      setContent(data);
      setStatus({ type: 'success', text: 'Default məzmuna qaytarıldı' });
    } catch (err) {
      setStatus({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setContent({ ...content, [key]: value });

  const updateArrayItem = (arrayKey, index, field, value) => {
    const arr = [...content[arrayKey]];
    arr[index] = { ...arr[index], [field]: value };
    setContent({ ...content, [arrayKey]: arr });
  };

  const addArrayItem = (arrayKey, defaultItem) => {
    setContent({ ...content, [arrayKey]: [...(content[arrayKey] || []), defaultItem] });
  };

  const removeArrayItem = (arrayKey, index) => {
    const arr = [...content[arrayKey]];
    arr.splice(index, 1);
    setContent({ ...content, [arrayKey]: arr });
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600"></div>
      </div>
    );
  }

  const inputCls = "w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition";
  const labelCls = "block text-sm font-medium text-stone-700 mb-1";

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top bar */}
      <nav className="bg-white shadow-sm border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-stone-900" data-testid="landing-admin-title">
                🎨 Sayt Redaktoru
              </h1>
              <span className="text-sm text-stone-500 hidden md:inline">{user?.email}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => window.open('/', '_blank')} className="hidden sm:inline-flex px-3 py-1.5 text-sm bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition" data-testid="preview-button">
                👁️ Sayta bax
              </button>
              <button onClick={() => navigate('/dashboard')} className="hidden sm:inline-flex px-3 py-1.5 text-sm bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition">
                Restoran paneli
              </button>
              <button onClick={resetDefaults} className="hidden md:inline-flex px-3 py-1.5 text-sm bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg transition">
                ↻ Default
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition disabled:opacity-50"
                data-testid="save-button"
              >
                {saving ? 'Saxlanılır...' : '💾 Yadda saxla'}
              </button>
              <button onClick={async () => { await logout(); navigate('/login'); }} className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition">
                Çıxış
              </button>
            </div>
          </div>
        </div>
      </nav>

      {status && (
        <div className={`max-w-7xl mx-auto px-4 mt-4 ${status.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
          <div className={`p-3 rounded-xl ${status.type === 'success' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`} data-testid="status-message">
            {status.text}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="bg-white rounded-2xl shadow-sm p-3 space-y-1">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition flex items-center space-x-2 ${
                  activeSection === s.id ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'hover:bg-stone-50 text-stone-700'
                }`}
                data-testid={`section-tab-${s.id}`}
              >
                <span>{s.icon}</span>
                <span className="text-sm">{s.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Content */}
        <main className="bg-white rounded-2xl shadow-sm p-6">
          {/* BRANDING */}
          {activeSection === 'branding' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">🏷️ Brend</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Sayt adı</label>
                  <input className={inputCls} value={content.site_name} onChange={(e) => update('site_name', e.target.value)} data-testid="site-name-input" />
                </div>
                <div>
                  <label className={labelCls}>Domen sonluğu (.az, .com, ...)</label>
                  <input className={inputCls} value={content.site_tld} onChange={(e) => update('site_tld', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Brauzer tab başlığı (Səhifə açılanda görünən mətn)</label>
                <input
                  className={inputCls}
                  value={content.browser_title || ''}
                  onChange={(e) => update('browser_title', e.target.value)}
                  placeholder="SmartMenyu.az — Restoran & Cafe QR Menyu Platforması"
                  data-testid="browser-title-input"
                />
                <p className="mt-1 text-xs text-stone-500">
                  Bu mətn brauzer tabında və axtarış nəticələrində görünür. Yaxşı SEO üçün 50-60 simvol tövsiyə olunur.
                </p>
              </div>
            </div>
          )}

          {/* NAVBAR */}
          {activeSection === 'navbar' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">🧭 Navbar (Yuxarı menyu)</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>"Xüsusiyyətlər" mətni</label>
                  <input className={inputCls} value={content.nav_features} onChange={(e) => update('nav_features', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>"Necə işləyir" mətni</label>
                  <input className={inputCls} value={content.nav_how} onChange={(e) => update('nav_how', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>"Əlaqə" mətni</label>
                  <input className={inputCls} value={content.nav_contact} onChange={(e) => update('nav_contact', e.target.value)} />
                </div>
              </div>

              <div className="border-t pt-5 mt-5">
                <h3 className="font-bold text-stone-800 mb-3">🔘 Navbar Düyməsi (sağ üstdəki yaşıl düymə)</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Düymə mətni</label>
                    <input className={inputCls} value={content.nav_cta} onChange={(e) => update('nav_cta', e.target.value)} placeholder="Əlaqə saxla" />
                  </div>
                  <div>
                    <label className={labelCls}>Link (URL və ya #anchor)</label>
                    <input className={inputCls} value={content.nav_cta_url || ''} onChange={(e) => update('nav_cta_url', e.target.value)} placeholder="#contact və ya https://..." />
                    <p className="text-xs text-stone-500 mt-1">
                      💡 İpucu: Səhifədaxili keçid üçün <code>#contact</code>, xarici link üçün <code>https://...</code>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* HERO */}
          {activeSection === 'hero' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">🎯 Hero (Əsas bölmə)</h2>
              <div>
                <label className={labelCls}>Yuxarı kiçik etiket</label>
                <input className={inputCls} value={content.hero_badge} onChange={(e) => update('hero_badge', e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Başlıq - Sətir 1</label>
                  <input className={inputCls} value={content.hero_title_line1} onChange={(e) => update('hero_title_line1', e.target.value)} data-testid="hero-line1-input" />
                </div>
                <div>
                  <label className={labelCls}>Başlıq - Sətir 2 (vurğulanan)</label>
                  <input className={inputCls} value={content.hero_title_line2} onChange={(e) => update('hero_title_line2', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Alt mətn (paraqraf)</label>
                <textarea className={inputCls} rows="3" value={content.hero_subtitle} onChange={(e) => update('hero_subtitle', e.target.value)}></textarea>
              </div>
              <div>
                <label className={labelCls}>CTA Düyməsi mətni</label>
                <input className={inputCls} value={content.hero_cta_text} onChange={(e) => update('hero_cta_text', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Arxa fon şəkli URL</label>
                <input className={inputCls} value={content.hero_bg_url} onChange={(e) => update('hero_bg_url', e.target.value)} placeholder="https://..." />
              </div>

              {/* HERO BUTTONS */}
              <div className="border-t pt-5 mt-5">
                <h3 className="font-bold text-stone-800 mb-3">🔘 Hero Düymələri</h3>

                {/* Primary CTA */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-emerald-800 mb-3">1️⃣ Əsas Düymə (yaşıl)</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Düymə mətni</label>
                      <input className={inputCls} value={content.hero_cta_text} onChange={(e) => update('hero_cta_text', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Link (URL və ya #anchor)</label>
                      <input className={inputCls} value={content.hero_cta_url || ''} onChange={(e) => update('hero_cta_url', e.target.value)} placeholder="#contact" data-testid="hero-cta-url-input" />
                    </div>
                  </div>
                </div>

                {/* Secondary CTA */}
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-stone-800">2️⃣ İkinci Düymə (outline - şəffaf)</p>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!content.hero_secondary_enabled}
                        onChange={(e) => update('hero_secondary_enabled', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded"
                        data-testid="hero-secondary-toggle"
                      />
                      <span className="text-sm font-medium">Aktiv et</span>
                    </label>
                  </div>
                  {content.hero_secondary_enabled && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Düymə mətni</label>
                        <input className={inputCls} value={content.hero_secondary_text || ''} onChange={(e) => update('hero_secondary_text', e.target.value)} placeholder="Daha çox" />
                      </div>
                      <div>
                        <label className={labelCls}>Link</label>
                        <input className={inputCls} value={content.hero_secondary_url || ''} onChange={(e) => update('hero_secondary_url', e.target.value)} placeholder="#features" />
                      </div>
                    </div>
                  )}
                  {!content.hero_secondary_enabled && (
                    <p className="text-xs text-stone-500">İkinci düymə deaktivdir. Yuxarıdakı qutuyu işarələyin.</p>
                  )}
                </div>

                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                  💡 <strong>Link növləri:</strong><br/>
                  • Səhifədaxili: <code className="bg-blue-100 px-1 rounded">#contact</code>, <code className="bg-blue-100 px-1 rounded">#features</code>, <code className="bg-blue-100 px-1 rounded">#how-it-works</code><br/>
                  • Xarici sayt: <code className="bg-blue-100 px-1 rounded">https://example.com</code><br/>
                  • Telefon: <code className="bg-blue-100 px-1 rounded">tel:+994501234567</code><br/>
                  • Email: <code className="bg-blue-100 px-1 rounded">mailto:info@smartmenyu.az</code>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-stone-800">📊 Statistikalar</label>
                  <button type="button" onClick={() => addArrayItem('hero_stats', { value: '0', label: 'Yeni' })} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                    + Əlavə et
                  </button>
                </div>
                <div className="space-y-2">
                  {content.hero_stats?.map((stat, i) => (
                    <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                      <input className={inputCls} placeholder="Dəyər" value={stat.value} onChange={(e) => updateArrayItem('hero_stats', i, 'value', e.target.value)} />
                      <input className={inputCls} placeholder="Etiket" value={stat.label} onChange={(e) => updateArrayItem('hero_stats', i, 'label', e.target.value)} />
                      <button onClick={() => removeArrayItem('hero_stats', i)} className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MOCKUP */}
          {activeSection === 'mockup' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">📱 Telefon Mockup</h2>
              <p className="text-sm text-stone-600">Hero bölməsinin sağındakı telefon görüntüsündə görünən mətnlər</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Restoran adı (mockup)</label>
                  <input className={inputCls} value={content.mockup_restaurant_name} onChange={(e) => update('mockup_restaurant_name', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Alt yazı</label>
                  <input className={inputCls} value={content.mockup_subtitle} onChange={(e) => update('mockup_subtitle', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* FEATURES */}
          {activeSection === 'features' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">✨ Xüsusiyyətlər bölməsi</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Alt başlıq</label>
                  <input className={inputCls} value={content.features_subtitle} onChange={(e) => update('features_subtitle', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Başlıq - 1-ci hissə</label>
                  <input className={inputCls} value={content.features_title_part1} onChange={(e) => update('features_title_part1', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Başlıq - vurğulanan</label>
                  <input className={inputCls} value={content.features_title_highlight} onChange={(e) => update('features_title_highlight', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Təsvir mətni</label>
                <textarea className={inputCls} rows="2" value={content.features_description} onChange={(e) => update('features_description', e.target.value)}></textarea>
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-stone-800">🎴 Xüsusiyyət kartları</h3>
                  <button type="button" onClick={() => addArrayItem('features', { icon: '⭐', title: 'Yeni', description: 'Təsvir', color: 'from-emerald-500 to-emerald-700' })} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                    + Yeni xüsusiyyət
                  </button>
                </div>
                <div className="space-y-3">
                  {content.features?.map((feat, i) => (
                    <div key={i} className="border border-stone-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-stone-600">#{i + 1}</span>
                        <button onClick={() => removeArrayItem('features', i)} className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded">✕ Sil</button>
                      </div>
                      <div className="grid sm:grid-cols-[80px_1fr] gap-2 mb-2">
                        <input className={inputCls} placeholder="🎯" value={feat.icon} onChange={(e) => updateArrayItem('features', i, 'icon', e.target.value)} />
                        <input className={inputCls} placeholder="Başlıq" value={feat.title} onChange={(e) => updateArrayItem('features', i, 'title', e.target.value)} />
                      </div>
                      <textarea className={inputCls + ' mb-2'} rows="2" placeholder="Təsvir" value={feat.description} onChange={(e) => updateArrayItem('features', i, 'description', e.target.value)}></textarea>
                      <div>
                        <label className="text-xs text-stone-500 mb-1 block">Rəng</label>
                        <div className="flex flex-wrap gap-2">
                          {COLORS.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => updateArrayItem('features', i, 'color', color)}
                              className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} ${feat.color === color ? 'ring-2 ring-offset-2 ring-emerald-500' : ''}`}
                            ></button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* HOW IT WORKS */}
          {activeSection === 'how' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">🔄 Necə işləyir</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Alt başlıq</label>
                  <input className={inputCls} value={content.how_it_works_subtitle} onChange={(e) => update('how_it_works_subtitle', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Başlıq</label>
                  <input className={inputCls} value={content.how_it_works_title} onChange={(e) => update('how_it_works_title', e.target.value)} />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-stone-800">📋 Addımlar</h3>
                  <button type="button" onClick={() => addArrayItem('how_it_works_steps', { step: '0', title: 'Yeni addım', description: 'Təsvir' })} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                    + Addım
                  </button>
                </div>
                <div className="space-y-3">
                  {content.how_it_works_steps?.map((step, i) => (
                    <div key={i} className="border border-stone-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-stone-600">Addım #{i + 1}</span>
                        <button onClick={() => removeArrayItem('how_it_works_steps', i)} className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded">✕</button>
                      </div>
                      <div className="grid sm:grid-cols-[80px_1fr] gap-2 mb-2">
                        <input className={inputCls} placeholder="01" value={step.step} onChange={(e) => updateArrayItem('how_it_works_steps', i, 'step', e.target.value)} />
                        <input className={inputCls} placeholder="Başlıq" value={step.title} onChange={(e) => updateArrayItem('how_it_works_steps', i, 'title', e.target.value)} />
                      </div>
                      <input className={inputCls} placeholder="Təsvir" value={step.description} onChange={(e) => updateArrayItem('how_it_works_steps', i, 'description', e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CONTACT */}
          {activeSection === 'contact' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">📞 Əlaqə bölməsi</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Alt başlıq</label>
                  <input className={inputCls} value={content.contact_subtitle} onChange={(e) => update('contact_subtitle', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Başlıq</label>
                  <input className={inputCls} value={content.contact_title} onChange={(e) => update('contact_title', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Təsvir</label>
                <textarea className={inputCls} rows="2" value={content.contact_description} onChange={(e) => update('contact_description', e.target.value)}></textarea>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>📧 Email</label>
                  <input className={inputCls} value={content.contact_email} onChange={(e) => update('contact_email', e.target.value)} data-testid="contact-email-edit" />
                </div>
                <div>
                  <label className={labelCls}>📍 Ünvan</label>
                  <input className={inputCls} value={content.contact_address} onChange={(e) => update('contact_address', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Əlavə qeyd (sağdakı kart altında)</label>
                <textarea className={inputCls} rows="2" value={content.contact_info_note} onChange={(e) => update('contact_info_note', e.target.value)}></textarea>
              </div>
            </div>
          )}

          {/* FOOTER */}
          {activeSection === 'footer' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">🦶 Footer (Alt hissə)</h2>
              <div>
                <label className={labelCls}>Brend təsviri (sol blok)</label>
                <input className={inputCls} value={content.footer_tagline} onChange={(e) => update('footer_tagline', e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Copyright mətni</label>
                  <input className={inputCls} value={content.footer_copyright} onChange={(e) => update('footer_copyright', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Sağ alt yazı</label>
                  <input className={inputCls} value={content.footer_made_in} onChange={(e) => update('footer_made_in', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* SCRIPTS (Trackers) — unlimited list */}
          {activeSection === 'scripts' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-2xl font-bold text-stone-900">📊 Trekkerlər (Analitika və Xarici Skriptlər)</h2>
                <button
                  onClick={() => {
                    const list = Array.isArray(content.custom_scripts) ? content.custom_scripts : [];
                    const newItem = { id: `s_${Date.now()}`, name: '', placement: 'head', code: '', enabled: true };
                    update('custom_scripts', [...list, newItem]);
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
                  data-testid="add-script-btn"
                >
                  <span className="text-lg leading-none">+</span> Yeni skript
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                <p className="font-semibold mb-1">⚠️ Diqqət — Yalnız etibarlı mənbələrdən skriptlər əlavə edin.</p>
                <p>Aşağıdakı skriptlər saytınızın <strong>bütün</strong> səhifələrinə əlavə olunacaq. Hər birini <strong>on/off</strong> düyməsi ilə fərdi idarə edə bilərsiniz. İstənilən sayda skript əlavə edə bilərsiniz.</p>
              </div>

              {(!Array.isArray(content.custom_scripts) || content.custom_scripts.length === 0) && (
                <div className="text-center py-10 text-stone-500 text-sm border-2 border-dashed border-stone-200 rounded-xl">
                  Hələ skript əlavə edilməyib. <strong>+ Yeni skript</strong> düyməsi ilə başlayın.
                </div>
              )}

              <div className="space-y-4">
                {(content.custom_scripts || []).map((s, idx) => (
                  <div key={s.id || idx} className={`border rounded-xl p-4 transition ${s.enabled ? 'border-stone-200 bg-white' : 'border-stone-200 bg-stone-50 opacity-70'}`} data-testid={`script-item-${idx}`}>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <input
                        type="text"
                        placeholder="Skript adı (məs: Google Analytics)"
                        value={s.name || ''}
                        onChange={(e) => {
                          const list = [...content.custom_scripts];
                          list[idx] = { ...list[idx], name: e.target.value };
                          update('custom_scripts', list);
                        }}
                        className="flex-1 min-w-[180px] px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        data-testid={`script-name-${idx}`}
                      />
                      <select
                        value={s.placement || 'head'}
                        onChange={(e) => {
                          const list = [...content.custom_scripts];
                          list[idx] = { ...list[idx], placement: e.target.value };
                          update('custom_scripts', list);
                        }}
                        className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white"
                        data-testid={`script-placement-${idx}`}
                      >
                        <option value="head">&lt;head&gt;</option>
                        <option value="body">&lt;/body&gt; öncəsi</option>
                      </select>
                      <label className="flex items-center gap-2 cursor-pointer text-sm px-3 py-2 rounded-lg hover:bg-stone-100" data-testid={`script-toggle-${idx}`}>
                        <input
                          type="checkbox"
                          checked={!!s.enabled}
                          onChange={(e) => {
                            const list = [...content.custom_scripts];
                            list[idx] = { ...list[idx], enabled: e.target.checked };
                            update('custom_scripts', list);
                          }}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        <span className={s.enabled ? 'text-emerald-700 font-semibold' : 'text-stone-500'}>{s.enabled ? 'Aktiv' : 'Passiv'}</span>
                      </label>
                      <button
                        onClick={() => {
                          if (!window.confirm(`"${s.name || 'Adsız skript'}" silinsin?`)) return;
                          const list = content.custom_scripts.filter((_, i) => i !== idx);
                          update('custom_scripts', list);
                        }}
                        className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium border border-red-200"
                        data-testid={`script-delete-${idx}`}
                      >
                        🗑 Sil
                      </button>
                    </div>
                    <textarea
                      rows="8"
                      value={s.code || ''}
                      onChange={(e) => {
                        const list = [...content.custom_scripts];
                        list[idx] = { ...list[idx], code: e.target.value };
                        update('custom_scripts', list);
                      }}
                      placeholder={'<!-- Nümunə: Google Analytics gtag skripti -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag(\'js\', new Date());\n  gtag(\'config\', \'G-XXXXXXX\');\n</script>'}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      spellCheck={false}
                      data-testid={`script-code-${idx}`}
                    />
                  </div>
                ))}
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
                <p className="font-semibold mb-1.5">✅ Populyar xidmətlər üçün nümunələr:</p>
                <ul className="space-y-1 text-xs list-disc pl-5">
                  <li><strong>Google Analytics 4</strong>: analytics.google.com → Data Streams → gtag skriptini kopyalayın</li>
                  <li><strong>Yandex Metrica</strong>: metrica.yandex.com → Sayğac yaradın → HTML kodu</li>
                  <li><strong>Facebook Pixel</strong>: business.facebook.com/events_manager → Pixel yaradın</li>
                  <li><strong>Tawk.to canlı chat</strong>: tawk.to (pulsuz) → placement: body</li>
                  <li><strong>Microsoft Clarity</strong>: clarity.microsoft.com (pulsuz heatmap)</li>
                </ul>
              </div>
            </div>
          )}

          {/* SOCIALS — floating widget */}
          {activeSection === 'socials' && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-stone-900">💬 Sosial Şəbəkələr (Aşağı sağ widget)</h2>
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm text-stone-700">
                <p>Buraya URL yazdığınız və <strong>Aktiv</strong> etdiyiniz hər platforma landing səhifənin <strong>aşağı sağ küncündə</strong> gözəl bir widget kimi görünəcək. Yazmadıqlarınız gizli qalacaq. URL boş buraxılsa, on/off təsir etməyəcək.</p>
              </div>

              <div className="space-y-3">
                {SOCIAL_PLATFORMS.map((p) => {
                  const list = Array.isArray(content.social_links) ? content.social_links : [];
                  const idx = list.findIndex((x) => x.platform === p.key);
                  const item = idx >= 0 ? list[idx] : { platform: p.key, url: '', label: '', enabled: false };
                  const setItem = (patch) => {
                    const next = idx >= 0 ? [...list] : [...list, item];
                    const at = idx >= 0 ? idx : next.length - 1;
                    next[at] = { ...next[at], ...patch };
                    update('social_links', next);
                  };
                  return (
                    <div key={p.key} className={`border rounded-xl p-4 transition ${item.enabled && item.url ? 'border-emerald-200 bg-emerald-50/40' : 'border-stone-200 bg-white'}`} data-testid={`social-item-${p.key}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-stone-900 text-white flex items-center justify-center text-lg">
                          <i className={`fa-brands fa-${p.key === 'twitter' ? 'x-twitter' : p.key}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-stone-900">{p.name}</div>
                          <input
                            type="url"
                            placeholder={p.example}
                            value={item.url || ''}
                            onChange={(e) => setItem({ url: e.target.value })}
                            className="mt-1 w-full px-3 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            data-testid={`social-url-${p.key}`}
                          />
                          <input
                            type="text"
                            placeholder={`Widget mətni (məs: "Bizi ${p.name}-da izləyin")`}
                            value={item.label || ''}
                            onChange={(e) => setItem({ label: e.target.value })}
                            className="mt-1.5 w-full px-3 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            data-testid={`social-label-${p.key}`}
                          />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer text-sm px-3 py-2 rounded-lg hover:bg-stone-100">
                          <input
                            type="checkbox"
                            checked={!!item.enabled}
                            onChange={(e) => setItem({ enabled: e.target.checked })}
                            className="w-4 h-4 accent-emerald-600"
                            data-testid={`social-toggle-${p.key}`}
                          />
                          <span className={item.enabled ? 'text-emerald-700 font-semibold' : 'text-stone-500'}>{item.enabled ? 'Aktiv' : 'Passiv'}</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
